import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Technology from "@/pages/Technology";
import Products from "@/pages/Products";
import Generator from "@/pages/Generator";
import Projects from "@/pages/Projects";
import Sustainability from "@/pages/Sustainability";
import Innovation from "@/pages/Innovation";
import News from "@/pages/News";
import NewsArticle from "@/pages/NewsArticle";
import Gallery from "@/pages/Gallery";
import PressKit from "@/pages/PressKit";
import Partners from "@/pages/Partners";
import Contact from "@/pages/Contact";
import { Privacy, Terms } from "@/pages/Legal";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import Solar from "@/pages/Solar";
import SolarCalculator from "@/pages/SolarCalculator";
import SolarReport from "@/pages/SolarReport";
import SolarDashboard from "@/pages/SolarDashboard";
import Energy from "@/pages/Energy";
import EnergyEngine from "@/pages/EnergyEngine";
import EnergyCompare from "@/pages/EnergyCompare";
import EnergyOutage from "@/pages/EnergyOutage";
import EnergyPresets from "@/pages/EnergyPresets";
import EnergyBattery from "@/pages/EnergyBattery";
import EnergySafety from "@/pages/EnergySafety";
import EnergyEmergency from "@/pages/EnergyEmergency";
import EnergyMap from "@/pages/EnergyMap";
import EnergyFinancing from "@/pages/EnergyFinancing";
import EnergyCfg from "@/pages/EnergyCfg";
import EnergyLearn from "@/pages/EnergyLearn";
import NotFound from "@/pages/NotFound";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ScrollManager />
      <Navbar />
      <main id="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/products" element={<Products />} />
          <Route path="/carbon-free-generator" element={<Generator />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/innovation" element={<Innovation />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/press" element={<PressKit />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/solar" element={<Solar />} />
          <Route path="/solar/calculator" element={<SolarCalculator />} />
          <Route path="/solar/report" element={<SolarReport />} />
          <Route path="/solar/dashboard" element={<SolarDashboard />} />
          <Route path="/energy" element={<Energy />} />
          <Route path="/energy/engine" element={<EnergyEngine />} />
          <Route path="/energy/compare" element={<EnergyCompare />} />
          <Route path="/energy/outage" element={<EnergyOutage />} />
          <Route path="/energy/presets/:kind" element={<EnergyPresets />} />
          <Route path="/energy/battery" element={<EnergyBattery />} />
          <Route path="/energy/safety" element={<EnergySafety />} />
          <Route path="/energy/emergency" element={<EnergyEmergency />} />
          <Route path="/energy/map" element={<EnergyMap />} />
          <Route path="/energy/financing" element={<EnergyFinancing />} />
          <Route path="/energy/carbon-free-generator" element={<EnergyCfg />} />
          <Route path="/energy/learn" element={<EnergyLearn />} />
          <Route path="/energy/learn/:slug" element={<EnergyLearn />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
      <Toaster position="top-right" richColors closeButton />
    </BrowserRouter>
  );
}
